import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, Lock, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { useSecurity } from "@/security/ArmadilloProvider";
import { adminAuthService } from "@/services/adminAuthService";

interface AdminSecureAccessProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_ATTEMPTS = 5;
const STORAGE_KEY = 'admin_lockout';

// Read persisted lockout state from localStorage
const getPersistedLockout = (): { locked: boolean; attempts: number; lockedAt: number | null; remainingMs: number } => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      const elapsed = Date.now() - data.lockedAt;
      if (elapsed < LOCKOUT_DURATION) {
        return { locked: true, attempts: data.attempts, lockedAt: data.lockedAt, remainingMs: LOCKOUT_DURATION - elapsed };
      }
      // Lockout expired — clean up
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
  return { locked: false, attempts: 0, lockedAt: null, remainingMs: 0 };
};

// Write lockout state to localStorage
const persistLockout = (attempts: number) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ attempts, lockedAt: Date.now() }));
};

const clearLockout = () => {
  localStorage.removeItem(STORAGE_KEY);
};

const AdminSecureAccess = ({ onSuccess, onCancel }: AdminSecureAccessProps) => {
  const navigate = useNavigate();
  const { validateAction, logSecurityEvent } = useSecurity();
  const [pin, setPin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [step, setStep] = useState<'pin' | 'password'>('pin');
  const [hasPassword, setHasPassword] = useState(false);

  const ADMIN_PIN = "476900"; // Your admin PIN

  // Initialize lockout state from localStorage on mount
  useEffect(() => {
    const persisted = getPersistedLockout();
    if (persisted.locked) {
      setLocked(true);
      setAttempts(persisted.attempts);
      setLockedUntil(new Date(Date.now() + persisted.remainingMs));
    }
  }, []);

  useEffect(() => {
    
    // Check if password is configured in database
    const checkDatabase = async () => {
      const hasCreds = await adminAuthService.hasCredentials();
      const securitySetup = localStorage.getItem('admin_security_setup');
      if (hasCreds || securitySetup === 'true') {
        setHasPassword(true);
        // Also fetch and store admin email if missing from localStorage
        if (!localStorage.getItem('admin_email')) {
          const adminEmail = await adminAuthService.getAdminEmail();
          if (adminEmail) {
            localStorage.setItem('admin_email', adminEmail);
          }
        }
      }
    };
    checkDatabase();
  }, []);

  const handlePINSubmit = async () => {
    if (pin.length !== 6) {
      toast.error("PIN must be 6 digits");
      return;
    }

    setLoading(true);
    
    // Armadillo rate limiting
    // Rate limiting handled by Armadillo Adamantium
    if (attempts >= MAX_ATTEMPTS) {
      toast.error("Too many attempts. Please wait.");
      setLoading(false);
      return;
    }

    // Security delay (increases with attempts)
    await new Promise(resolve => setTimeout(resolve, 300 * (attempts + 1)));

    if (pin === ADMIN_PIN) {
      // Verify PIN against database
      const pinResult = await adminAuthService.verifyPIN(pin);
      
      if (!pinResult.valid) {
        toast.error("PIN verification failed. Please setup security first.");
        setLoading(false);
        return;
      }

      // Store admin email from DB for password verification step
      if (pinResult.email) {
        localStorage.setItem('admin_email', pinResult.email);
      }

      console.log('🔒 Admin authenticated successfully');
      
      // Log successful auth to database
      await logSecurityEvent(
        'admin_auth_success',
        'low',
        'Admin authenticated successfully with PIN',
        { method: 'pin', step: step }
      );

      // If password is configured, move to password step
      if (hasPassword) {
        setStep('password');
        toast.success("✅ PIN verified! Now enter your password");
      } else {
        // No password set, just store PIN verification
        sessionStorage.setItem('admin_pin_verified', 'true');
        sessionStorage.setItem('admin_pin_verified_at', Date.now().toString());

        toast.success("🎉 Access granted!", { description: "Welcome, Administrator" });
        
        setTimeout(() => {
          onSuccess();
          navigate("/ops-command");
        }, 800);
      }
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      console.log(`🔒 Invalid PIN attempt ${newAttempts}/${MAX_ATTEMPTS}`);
      
      // Log failed attempt to database
      await logSecurityEvent(
        'admin_auth_failed',
        'medium',
        `Invalid admin PIN attempt ${newAttempts}/${MAX_ATTEMPTS}`,
        { attempts: newAttempts, maxAttempts: MAX_ATTEMPTS }
      );

      if (newAttempts >= MAX_ATTEMPTS) {
        setLocked(true);
        setLockedUntil(new Date(Date.now() + LOCKOUT_DURATION));
        const lockoutDate = new Date(Date.now() + LOCKOUT_DURATION);
        persistLockout(newAttempts); // Persist to localStorage
        toast.error("🔒 Access locked", { 
          description: `Too many failed attempts. Try again at ${lockoutDate.toLocaleTimeString()}.` 
        });
      } else {
        toast.error(`Invalid PIN (${MAX_ATTEMPTS - newAttempts} attempts remaining)`);
      }
    }
    
    setLoading(false);
    setPin("");
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    // Check for password lockout too
    const pwStorageKey = 'admin_password_lockout';
    const pwLockout = (() => {
      try {
        const stored = localStorage.getItem(pwStorageKey);
        if (stored) {
          const data = JSON.parse(stored);
          if (Date.now() - data.lockedAt < LOCKOUT_DURATION) {
            return { locked: true, attempts: data.attempts };
          }
          localStorage.removeItem(pwStorageKey);
        }
      } catch {}
      return { locked: false, attempts: 0 };
    })();

    if (pwLockout.locked) {
      toast.error(`Too many password attempts. Please wait. (${pwLockout.attempts}/${MAX_ATTEMPTS})`);
      return;
    }

    setLoading(true);

    try {
      // Get admin email from localStorage
      const adminEmail = localStorage.getItem('admin_email');
      
      if (!adminEmail) {
        toast.error("Admin email not found. Please setup security first.");
        setLoading(false);
        return;
      }

      // Verify password against database
      const isValid = await adminAuthService.verifyPassword(adminEmail, password);

      if (isValid) {
        // On success, clear password lockout
        localStorage.removeItem(pwStorageKey);
        
        console.log('🔒 Admin authenticated with PIN + Password');
        
        // Log successful auth to database
        await logSecurityEvent(
          'admin_auth_success',
          'low',
          'Admin authenticated with PIN + Password',
          { method: 'pin+password' }
        );

        // Store both PIN and password verification
        sessionStorage.setItem('admin_pin_verified', 'true');
        sessionStorage.setItem('admin_pin_verified_at', Date.now().toString());
        sessionStorage.setItem('admin_password_verified', 'true');

        toast.success("🎉 Access granted!", { description: "Welcome, Administrator" });

        setTimeout(() => {
          onSuccess();
          navigate("/ops-command");
        }, 800);
      } else {
        // Track password attempts
        const pwStorage = localStorage.getItem(pwStorageKey);
        const pwAttempts = pwStorage ? (JSON.parse(pwStorage).attempts + 1) : 1;
        
        if (pwAttempts >= MAX_ATTEMPTS) {
          localStorage.setItem(pwStorageKey, JSON.stringify({ attempts: pwAttempts, lockedAt: Date.now() }));
          const unlockTime = new Date(Date.now() + LOCKOUT_DURATION);
          toast.error("🔒 Password locked", {
            description: `Too many failed password attempts. Try again at ${unlockTime.toLocaleTimeString()}.`
          });
        } else {
          localStorage.setItem(pwStorageKey, JSON.stringify({ attempts: pwAttempts, lockedAt: Date.now() }));
          toast.error(`Incorrect password (${MAX_ATTEMPTS - pwAttempts} attempts remaining)`);
        }
      }
    } catch (error: any) {
      toast.error("Authentication failed: " + error.message);
    } finally {
      setLoading(false);
      setPassword("");
    }
  };

  if (locked) {
    const remainingMs = lockedUntil ? lockedUntil.getTime() - Date.now() : 0;
    const remainingMinutes = Math.max(0, Math.ceil(remainingMs / 60000));
    
    return (
      <Card className="p-8 max-w-md mx-auto">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-16 w-16 mx-auto text-red-500" />
          <h2 className="text-2xl font-bold text-red-600">Access Locked</h2>
          <p className="text-muted-foreground">
            Too many failed attempts. Please try again in {remainingMinutes} minute{remainingMinutes !== 1 ? 's' : ''}.
          </p>
          {lockedUntil && (
            <p className="text-xs text-muted-foreground">
              Unlocks at {lockedUntil.toLocaleTimeString()}
            </p>
          )}
          <p className="text-xs text-amber-600">
            This lockout persists even if you reload or close the page.
          </p>
          <Button onClick={onCancel} variant="outline">
            Return to Homepage
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-8 max-w-md mx-auto">
      {step === 'pin' ? (
        <>
          {/* PIN Step */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Shield className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Admin Access</h2>
            <p className="text-sm text-muted-foreground">
              Enter 6-digit security PIN
            </p>
          </div>

          <div className="space-y-4">
            <div className="text-center">
              <Lock className="h-10 w-10 mx-auto text-primary mb-3" />
            </div>
            
            <Input
              type="password"
              placeholder="Enter PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              className="text-center text-2xl tracking-widest"
              onKeyDown={(e) => e.key === 'Enter' && handlePINSubmit()}
              autoFocus
            />
            
            <Button
              onClick={handlePINSubmit}
              disabled={loading || pin.length !== 6}
              className="w-full"
              size="lg"
            >
              {loading ? "Verifying..." : "Access Admin Panel"}
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t space-y-3">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-3 w-3" />
              <span>Protected by Armadillo Security</span>
            </div>
            {attempts > 0 && (
              <p className="text-xs text-center text-amber-600">
                {MAX_ATTEMPTS - attempts} attempts remaining
              </p>
            )}
          </div>

          <Button onClick={onCancel} variant="ghost" className="w-full mt-2">
            Cancel
          </Button>
        </>
      ) : (
        <>
          {/* Password Step */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Lock className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Password Required</h2>
            <p className="text-sm text-muted-foreground">
              Enter your admin password
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                </button>
              </div>
            </div>
            
            <Button
              onClick={handlePasswordSubmit}
              disabled={loading || !password}
              className="w-full"
              size="lg"
            >
              {loading ? "Verifying..." : "Continue"}
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Shield className="h-3 w-3" />
              <span>Protected by Armadillo Security</span>
            </div>
          </div>

          <Button
            onClick={() => setStep('pin')}
            variant="ghost"
            className="w-full mt-2"
          >
            ← Back to PIN
          </Button>
        </>
      )}
    </Card>
  );
};

export default AdminSecureAccess;
